/**
 * Centre d'aide — coordonne hero + FAQ (menu+articles) + contact.
 * Recherche tolérante (stop-words FR/EN + racines).
 */
(function () {
  const STOP = new Set([
    'comment', 'combien', 'pourquoi', 'quand', 'quel', 'quelle', 'quels', 'quelles',
    'je', 'j', 'tu', 'vous', 'nous', 'on', 'mon', 'ma', 'mes', 'ton', 'ta', 'tes',
    'son', 'sa', 'ses', 'notre', 'nos', 'votre', 'vos', 'leur', 'leurs',
    'un', 'une', 'des', 'le', 'la', 'les', 'de', 'du', 'au', 'aux', 'en', 'et', 'ou',
    'a', 'à', 'pour', 'par', 'sur', 'sous', 'avec', 'sans', 'dans', 'chez', 'vers',
    'que', 'qui', 'quoi', 'dont', 'ce', 'cet', 'cette', 'ces', 'se', 'si', 'ne', 'pas',
    'plus', 'moins', 'tres', 'très', 'aussi', 'donc', 'alors', 'mais', 'car',
    'the', 'a', 'an', 'my', 'your', 'our', 'how', 'to', 'do', 'does', 'is', 'are',
    'can', 'i', 'me', 'what', 'when', 'where', 'why', 'which', 'of', 'for', 'in', 'on',
  ]);

  const normalize = (s) =>
    (s || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/['’]/g, ' ');

  const stem = (t) =>
    t
      .replace(/(isations|isation|issements|issement|ements|ement|ments|ment)$/i, '')
      .replace(/(atrices|atrice|ateurs|ateur|euses|euse|eurs|eur)$/i, '')
      .replace(/(tions|tion|sions|sion)$/i, '')
      .replace(/(elles|elle|eaux|eau|aux)$/i, '')
      .replace(/(iques|ique|ives|ive|ifs|if)$/i, '')
      .replace(/(eres|ères|ere|ère|ees|ées|ee|ée|es|és|e|s)$/i, '')
      .replace(/(er|ir|re)$/i, '');

  const tokens = (q) =>
    normalize(q)
      .split(/[^a-z0-9]+/)
      .filter((t) => t.length >= 2 && !STOP.has(t))
      .map((t) => ({ raw: t, stem: stem(t) || t }));

  const fieldMatch = (field, term) => {
    if (!field) return false;
    if (field.includes(term.raw)) return true;
    if (term.stem.length >= 3 && field.includes(term.stem)) return true;
    const parts = field.split(/[^a-z0-9]+/);
    return parts.some((p) => p.startsWith(term.stem) || (term.stem.startsWith(p) && p.length >= 3));
  };

  class HelpCenterPage {
    constructor() {
      this.hero = document.querySelector('[data-hc-hero]');
      this.faq = document.querySelector('[data-hc-faq]');
      this.menu = document.querySelector('[data-hc-menu]');
      this.articlesRoot = document.querySelector('[data-hc-articles]');
      if (!this.articlesRoot && !this.menu && !this.hero && !this.faq) return;

      this.categories = Array.from(document.querySelectorAll('[data-hc-category]'));
      this.panels = Array.from(document.querySelectorAll('[data-hc-panel]'));
      this.articles = Array.from(document.querySelectorAll('[data-hc-article]'));
      this.searchRoot = document.querySelector('[data-hc-search]');
      this.input = document.querySelector('[data-hc-input]');
      this.clearBtn = document.querySelector('[data-hc-clear]');
      this.suggestMeta = document.querySelector('[data-hc-suggest-meta]');
      this.suggestList = document.querySelector('[data-hc-suggest-list]');
      this.emptyEl = document.querySelector('[data-hc-empty]');
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.catMeta = {};
      this.categories.forEach((btn) => {
        const id = btn.dataset.hcCategory;
        if (!id) return;
        this.catMeta[id] = {
          label: btn.dataset.hcLabel || btn.textContent.trim(),
          icon: btn.dataset.hcIcon || 'star',
        };
      });
      this.panels.forEach((panel) => {
        const handle = panel.dataset.hcPanel;
        if (!handle) return;
        const label =
          panel.dataset.hcCatLabel ||
          (this.catMeta[handle] && this.catMeta[handle].label) ||
          handle;
        if (!this.catMeta[handle]) {
          this.catMeta[handle] = { label, icon: panel.dataset.hcCatIcon || 'star' };
        } else {
          this.catMeta[handle].label = label;
        }
        panel.querySelectorAll('[data-hc-article]').forEach((el) => {
          el.dataset.hcCat = handle;
          el.dataset.hcCatLabel = label;
        });
      });

      this.activeCategory =
        (this.categories[0] && this.categories[0].dataset.hcCategory) ||
        (this.panels[0] && this.panels[0].dataset.hcPanel) ||
        '';

      const openFirst =
        (this.faq && this.faq.dataset.openFirst === 'true') ||
        (this.articlesRoot && this.articlesRoot.dataset.openFirst === 'true');
      const firstInCategory = this.activeCategory
        ? this.articles.find((a) => a.dataset.hcCat === this.activeCategory)
        : this.articles[0];
      this.openId = openFirst && firstInCategory ? firstInCategory.dataset.hcArticle : null;

      this.#bind(signal);
      if (this.activeCategory) {
        this.#setCategory(this.activeCategory, { preserveOpen: true });
      } else if (this.panels[0]) {
        this.panels[0].classList.add('is-active');
      }
      if (this.openId) this.#setOpen(this.openId, { scroll: false });
      if (this.faq) this.faq.classList.add('is-ready');
      else if (this.articlesRoot) this.articlesRoot.classList.add('is-ready');

      const hash = (window.location.hash || '').replace(/^#/, '');
      if (hash === 'ContactForm' || hash === 'contact-form') {
        this.#revealContactForm();
      } else if (hash) {
        let target = null;
        try {
          target = document.querySelector(`[data-hc-article="${CSS.escape(hash)}"]`);
        } catch (e) {
          target = document.getElementById(hash);
        }
        if (target) this.#goToArticle(target);
      }
    }

    destroy() {
      if (this.abort) this.abort.abort();
    }

    #bind(signal) {
      this.categories.forEach((btn) => {
        btn.addEventListener(
          'click',
          () => {
            this.#setCategory(btn.dataset.hcCategory);
            this.#setOpen(null);
          },
          { signal }
        );
      });

      this.articles.forEach((acc) => {
        const btn = acc.querySelector('[data-hc-qbtn]');
        if (!btn) return;
        btn.addEventListener(
          'click',
          () => {
            const id = acc.dataset.hcArticle;
            this.#setOpen(this.openId === id ? null : id);
          },
          { signal }
        );
      });

      document.addEventListener(
        'click',
        (e) => {
          const link = e.target.closest('[data-hc-show-form]');
          if (!link) return;
          e.preventDefault();
          this.#revealContactForm();
        },
        { signal }
      );

      if (this.input && this.searchRoot) {
        this.input.addEventListener('focus', () => this.#openSearch(true), { signal });
        this.input.addEventListener(
          'input',
          () => {
            this.#syncQueryUI();
            this.#renderSuggestions();
            this.#openSearch(true);
          },
          { signal }
        );
        if (this.clearBtn) {
          this.clearBtn.addEventListener(
            'click',
            () => {
              this.input.value = '';
              this.#syncQueryUI();
              this.#renderSuggestions();
              this.input.focus();
            },
            { signal }
          );
        }
        document.addEventListener(
          'pointerdown',
          (e) => {
            if (!this.searchRoot.contains(e.target)) this.#openSearch(false);
          },
          { capture: true, signal }
        );
      }
    }

    #openSearch(open) {
      if (!this.searchRoot) return;
      this.searchRoot.classList.toggle('is-open', open);
      this.searchRoot.classList.toggle('is-focus', open);
      if (open) this.#renderSuggestions();
    }

    #syncQueryUI() {
      if (!this.searchRoot || !this.input) return;
      this.searchRoot.classList.toggle('has-query', this.input.value.length > 0);
    }

    #setCategory(id, { preserveOpen = false } = {}) {
      if (!id) return;
      this.activeCategory = id;
      this.categories.forEach((btn) => {
        btn.setAttribute('aria-pressed', btn.dataset.hcCategory === id ? 'true' : 'false');
      });
      this.panels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.hcPanel === id);
      });
      if (!preserveOpen) this.#setOpen(null);
    }

    #setOpen(id, { scroll = false } = {}) {
      this.openId = id;
      this.articles.forEach((acc) => {
        const on = acc.dataset.hcArticle === id;
        acc.classList.toggle('is-open', on);
        const btn = acc.querySelector('[data-hc-qbtn]');
        if (btn) btn.setAttribute('aria-expanded', on ? 'true' : 'false');
        const panel = acc.querySelector('[data-hc-answer]');
        if (panel) panel.setAttribute('aria-hidden', on ? 'false' : 'true');
        const nudge = acc.querySelector('[data-hc-contact-nudge]');
        if (nudge) nudge.hidden = !on;
      });
      if (scroll && id) {
        let el = null;
        try {
          el = document.querySelector(`[data-hc-article="${CSS.escape(id)}"]`);
        } catch (e) {
          el = document.getElementById(id);
        }
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 32;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
      }
      if (id) {
        try {
          history.replaceState(null, '', `#${id}`);
        } catch (e) {
          /* ignore */
        }
      }
    }

    #revealContactForm() {
      if (typeof window.tbRevealContactForm === 'function') {
        window.tbRevealContactForm();
        return;
      }
      const host = document.querySelector('[data-hc-contact-form]');
      if (!host) return;
      host.hidden = false;
      host.classList.add('is-revealed');
      const top = host.getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    #goToArticle(el) {
      if (!el) return;
      this.#setCategory(el.dataset.hcCat, { preserveOpen: true });
      this.#setOpen(el.dataset.hcArticle, { scroll: true });
      if (this.input) {
        this.input.value = '';
        this.#syncQueryUI();
      }
      this.#openSearch(false);
    }

    #articleIndex() {
      return this.articles.map((el) => ({
        el,
        id: el.dataset.hcArticle,
        theme: el.dataset.hcCat,
        themeLabel:
          el.dataset.hcCatLabel ||
          (this.catMeta[el.dataset.hcCat] && this.catMeta[el.dataset.hcCat].label) ||
          '',
        q: el.dataset.hcQuestion || '',
        a: el.dataset.hcAnswerText || '',
        featured: el.dataset.hcFeatured === 'true',
      }));
    }

    #search(q, limit = 8) {
      let terms = tokens(q);
      if (!terms.length) {
        terms = normalize(q)
          .split(/[^a-z0-9]+/)
          .filter((t) => t.length >= 3)
          .map((t) => ({ raw: t, stem: stem(t) || t }));
      }
      if (!terms.length) return [];

      const scored = [];
      for (const it of this.#articleIndex()) {
        const qh = normalize(it.q);
        const ah = normalize(it.a);
        const th = normalize(it.themeLabel);
        let score = 0;
        let matched = 0;
        for (const t of terms) {
          let hit = false;
          if (fieldMatch(qh, t)) {
            hit = true;
            score += qh.startsWith(t.raw) ? 8 : 5;
          }
          if (fieldMatch(th, t)) {
            hit = true;
            score += 4;
          }
          if (fieldMatch(ah, t)) {
            hit = true;
            score += 1;
          }
          if (hit) matched += 1;
        }
        if (matched > 0) {
          score += matched * 2;
          if (terms.length > 1 && matched === terms.length) score += 6;
          scored.push({ ...it, score, matched });
        }
      }
      scored.sort((a, b) => b.score - a.score || a.q.length - b.q.length);
      return scored.slice(0, limit);
    }

    #highlight(text, query) {
      const terms = tokens(query)
        .map((t) => t.stem)
        .filter((t) => t.length >= 3);
      if (!terms.length) return this.#escape(text);
      const flat = normalize(text);
      const marks = [];
      for (const t of terms) {
        let i = flat.indexOf(t);
        while (i !== -1) {
          marks.push([i, i + t.length]);
          i = flat.indexOf(t, i + t.length);
        }
      }
      if (!marks.length) return this.#escape(text);
      marks.sort((a, b) => a[0] - b[0]);
      const merged = [marks[0]];
      for (const m of marks.slice(1)) {
        const last = merged[merged.length - 1];
        if (m[0] <= last[1]) last[1] = Math.max(last[1], m[1]);
        else merged.push(m);
      }
      let out = '';
      let cursor = 0;
      for (const [s, e] of merged) {
        out += this.#escape(text.slice(cursor, s));
        out += `<mark>${this.#escape(text.slice(s, e))}</mark>`;
        cursor = e;
      }
      out += this.#escape(text.slice(cursor));
      return out;
    }

    #escape(s) {
      return (s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    #renderSuggestions() {
      if (!this.suggestList || !this.input) return;
      const q = this.input.value;
      const active = normalize(q).trim().length >= 2;
      const email =
        (this.hero && this.hero.dataset.email) || 'bonjour@tajinebanane.fr';
      const emptyTpl =
        (this.hero && this.hero.dataset.emptyTemplate) || 'Aucun résultat pour « {query} ».';
      const emptyHelp = (this.hero && this.hero.dataset.emptyHelp) || '';

      this.suggestList.innerHTML = '';
      if (this.emptyEl) this.emptyEl.hidden = true;

      const index = this.#articleIndex();

      if (active) {
        if (this.suggestMeta) this.suggestMeta.hidden = true;
        const results = this.#search(q, 8);
        if (!results.length) {
          if (this.emptyEl) {
            this.emptyEl.hidden = false;
            this.emptyEl.innerHTML = `<p>${this.#escape(emptyTpl.replace('{query}', q))}</p>${
              emptyHelp
                ? `<p class="help-center__none-help">${emptyHelp.replace(
                    '{email}',
                    `<a href="mailto:${this.#escape(email)}">${this.#escape(email)}</a>`
                  )}</p>`
                : ''
            }`;
          }
          return;
        }
        for (const it of results) {
          this.suggestList.appendChild(this.#hitButton(it, q, true));
        }
        return;
      }

      if (this.suggestMeta) this.suggestMeta.hidden = false;
      const featured = index.filter((a) => a.featured).slice(0, 5);
      const fallback = featured.length ? featured : index.slice(0, 5);
      for (const it of fallback) {
        this.suggestList.appendChild(this.#hitButton(it, '', false));
      }
    }

    #hitButton(it, query, withMeta) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'help-center__hit';
      btn.innerHTML = `${
        withMeta
          ? `<div class="help-center__hitmeta help-center__hitmeta--inline">${this.#escape(it.themeLabel)}</div>`
          : ''
      }<div class="help-center__hitq">${query ? this.#highlight(it.q, query) : this.#escape(it.q)}</div>`;
      btn.addEventListener('click', () => this.#goToArticle(it.el));
      return btn;
    }
  }

  const boot = () => {
    const articleCount = document.querySelectorAll('[data-hc-article]').length;
    const hasUi =
      document.querySelector('[data-hc-faq]') ||
      document.querySelector('[data-hc-hero]') ||
      document.querySelector('[data-hc-search]');
    if (!hasUi) return;

    // Ré-init si la 1re passe n’avait pas encore les articles (éditeur / charge partielle)
    if (window.__helpCenterPage) {
      if (window.__helpCenterPage._articleCount === articleCount && articleCount > 0) return;
      if (typeof window.__helpCenterPage.destroy === 'function') {
        window.__helpCenterPage.destroy();
      }
    }
    const page = new HelpCenterPage();
    page._articleCount = articleCount;
    window.__helpCenterPage = page;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Filet de sécurité si les blocs theme arrivent un tick plus tard
  window.addEventListener('load', boot);
})();
